import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Loader2, Handshake, Check, X } from "lucide-react";

const STATUS_META = {
  student_review: { label: "بانتظار ردّك", tone: "bg-blue-50 text-blue-700 border-blue-100" },
  approved: { label: "قبلتَ الطلب", tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  rejected: { label: "رفضتَ الطلب", tone: "bg-red-50 text-red-700 border-red-100" },
};

export default function ContactRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [replyFor, setReplyFor] = useState(null); // { req, action }
  const [response, setResponse] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/student/contact-requests");
      setItems(data.requests || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.patch(`/student/contact-requests/${id}`, { action, response });
      toast.success(action === "accept" ? "تم قبول التواصل — سيصل الرد لجهة الاستقطاب" : "تم رفض الطلب");
      setReplyFor(null); setResponse("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  return (
    <DashboardLayout>
      <div data-testid="student-contact-requests">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">طلبات التواصل</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">جهات الاستقطاب المُهتمة بك</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            هذه الطلبات مرّت باعتماد إدارة المنصة قبل وصولها إليك. أنت من يقرّر — لا تُشارَك بيانات تواصلك مع أي جهة إلا بعد موافقتك الصريحة.
          </p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="student-cr-error" />
        ) : items.length === 0 ? (
          <EmptyState icon={Handshake} title="لا توجد طلبات تواصل" description="عند اهتمام جهة استقطاب بك سيصلك الطلب هنا بعد اعتماده من الإدارة." testid="student-cr-empty" />
        ) : (
          <div className="grid gap-3">
            {items.map((r) => {
              const meta = STATUS_META[r.status] || { label: r.status, tone: "bg-slate-100 text-slate-700" };
              const isPending = r.status === "student_review";
              return (
                <div key={r.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`student-cr-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-slate-500">جهة الاستقطاب</div>
                      <div className="font-heading font-bold text-slate-900">{r.recruiter_name}</div>
                    </div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${meta.tone}`}>{meta.label}</span>
                  </div>
                  <div className="mt-3 p-3 rounded-lg bg-cream-50 text-sm text-slate-700 whitespace-pre-wrap">
                    {r.message || "—"}
                  </div>
                  {r.admin_note && (
                    <div className="mt-2 text-xs text-slate-500">ملاحظة الإدارة: <span className="text-slate-700">{r.admin_note}</span></div>
                  )}
                  {isPending && (
                    <div className="mt-4 flex items-center gap-2">
                      <button onClick={() => { setReplyFor({ req: r, action: "accept" }); setResponse(""); }} disabled={busyId === r.id} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid={`student-cr-accept-${r.id}`}>
                        <Check className="h-4 w-4" /> قبول التواصل
                      </button>
                      <button onClick={() => { setReplyFor({ req: r, action: "reject" }); setResponse(""); }} disabled={busyId === r.id} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 disabled:opacity-60" data-testid={`student-cr-reject-${r.id}`}>
                        <X className="h-4 w-4" /> رفض
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {replyFor && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setReplyFor(null); }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="student-cr-reply-modal">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 text-lg">
                  {replyFor.action === "accept" ? "قبول التواصل" : "رفض الطلب"}
                </h3>
                <button onClick={() => setReplyFor(null)} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="إغلاق"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                <div className="text-sm text-slate-600">
                  {replyFor.action === "accept"
                    ? `سنشارك بيانات تواصلك (البريد والجوال إن وُجد) مع جهة «${replyFor.req.recruiter_name}».`
                    : `لن تُشارَك أي من بياناتك مع جهة «${replyFor.req.recruiter_name}».`}
                </div>
                <textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={3} placeholder="رسالة اختيارية للإدارة/الجهة" className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="student-cr-response" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setReplyFor(null)} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
                  <button
                    onClick={() => decide(replyFor.req.id, replyFor.action)}
                    disabled={busyId === replyFor.req.id}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-60 ${replyFor.action === "accept" ? "bg-teal-700 hover:bg-teal-600" : "bg-red-600 hover:bg-red-500"}`}
                    data-testid="student-cr-confirm"
                  >
                    {busyId === replyFor.req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : replyFor.action === "accept" ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    {replyFor.action === "accept" ? "أوافق" : "أرفض"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
